/**
 * Data hook for the Quick Review detail modal.
 * @module useQuickReviewModalData
 */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { QuickReviewService } from "../../../../api/services/quickReviewService";
import { QuickReviewDetailResponse } from "../../../../data-model/quickReviewModel";
import { RootState } from "../../../../redux/store/store";

/** Loading/data state for the Quick Review detail fetched on modal mount. */
export interface QuickReviewModalDataState {
  /** Worked vs newly ingested field comparison from the detail endpoint */
  data?: QuickReviewDetailResponse;
  /** True while the detail request is in flight */
  loading?: boolean;
  /** Safe error message when the detail request fails */
  error?: string;
}

/**
 * Fetches the Quick Review detail (worked vs newly ingested field comparison)
 * for the order id stored in the modal's redux metadata when the modal opens.
 *
 * @returns Detail response with loading and error state
 */
export const useQuickReviewModalData = () => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const orderId = metaData?.orderId ?? "";
  const [quickReviewDetailState, setQuickReviewDetailState] =
    useState<QuickReviewModalDataState>({
      data: undefined,
      loading: false,
      error: undefined,
    });

  useEffect(() => {
    if (!orderId) return;

    let cancelled = false;
    setQuickReviewDetailState({ loading: true, error: undefined });

    const fetchDetail = async () => {
      try {
        const response = await QuickReviewService.detail({
          order_id: orderId,
        });
        if (!cancelled) {
          setQuickReviewDetailState({ data: response, loading: false });
        }
      } catch (error) {
        if (cancelled) return;
        setQuickReviewDetailState((prev) => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : String(error),
        }));
      }
    };

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return quickReviewDetailState;
};
